import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import UploadIcon from '../../../assets/icons/arrow_upward.svg?react';

interface AlarmSignalUploadDropzoneProps {
  onFileSelected?: (file: File) => void;
}

export function AlarmSignalUploadDropzone({
  onFileSelected,
}: AlarmSignalUploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isActive, setIsActive] = useState(false);

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  const handleFiles = (files: FileList | File[]) => {
    const file = Array.from(files)[0];
    if (!file) return;

    setSelectedFile(file);
    onFileSelected?.(file);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      handleFiles(event.target.files);
    }
    event.target.value = '';
  };

  const handleDragEnter = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsActive(true);
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsActive(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsActive(false);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsActive(false);
    if (event.dataTransfer.files.length > 0) {
      handleFiles(event.dataTransfer.files);
    }
  };

  return (
    <div className='flex flex-col gap-4 px-4 py-4 md:px-6'>
      <input
        ref={inputRef}
        type='file'
        accept='.png,.pdf,.jpg,.jpeg,image/png,image/jpeg,application/pdf'
        className='sr-only'
        onChange={handleInputChange}
      />

      <button
        type='button'
        onClick={openFilePicker}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-border-secondary flex min-h-60 w-full flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-10 text-center transition-colors outline-none focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:ring-inset ${
          isActive ? 'border-focus bg-surface-focus-support/10' : 'bg-surface-primary'
        }`}
      >
        <span
          className={`flex h-12 w-12 items-center justify-center rounded-2xl transition-colors ${
            isActive ? 'bg-surface-focus-support/30' : 'bg-surface-primary-support'
          }`}
        >
          <UploadIcon className='h-7 w-7 text-primary' />
        </span>

        <span className='mt-6 size-h4 text-primary font-bold'>
          Drag and drop or
        </span>
        <span className='size-h4 text-primary font-bold'>
          <span className='text-focus'>browse</span> your files
        </span>
        <span className='mt-2 size-p2 text-secondary'>
          png, pdf and jpeg files are supported
        </span>
      </button>

      {selectedFile && (
        <div className='border-border-secondary bg-surface-primary-support rounded-lg border px-4 py-3'>
          <div className='size-p2 font-bold text-primary'>{selectedFile.name}</div>
          <div className='size-caption text-secondary'>Selected file</div>
        </div>
      )}
    </div>
  );
}
